/**
 * The Kairelo logo.
 *
 * Inline SVG rather than `<img src="/logo.svg">` for two reasons: it inherits
 * the page's colour in the single-colour variants, so reversed and monochrome
 * need no second file; and it paints with the first frame rather than after a
 * second request, which matters most on the one page a stranger judges the
 * product by.
 *
 * The geometry is the same as `brand/logo/` — see `scripts/build-brand-logos.mjs`
 * for what every coordinate means. Anything changed here must be changed there
 * too, or the site and the brand assets drift apart.
 */

/** A gradient id must be unique per document, or the first one wins everywhere. */
let sequence = 0;

export function Logo({
  variant = 'gradient',
  size = 34,
  showWordmark = true,
  className,
}: {
  /** `gradient` for normal use; `mono` inherits `currentColor`. */
  variant?: 'gradient' | 'mono';
  /** Height of the symbol, in pixels. The wordmark is scaled to match. */
  size?: number;
  showWordmark?: boolean;
  className?: string;
}) {
  const id = `kairelo-logo-${(sequence += 1)}`;
  const paint = variant === 'mono' ? 'currentColor' : `url(#${id})`;

  const symbol = (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      role="img"
      aria-label={showWordmark ? '' : 'Kairelo'}
      aria-hidden={showWordmark || undefined}
      style={{ display: 'block', flex: 'none' }}
    >
      {variant === 'gradient' ? (
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#246bfd" />
            <stop offset="0.48" stopColor="#168ddc" />
            <stop offset="1" stopColor="#16c5ad" />
          </linearGradient>
        </defs>
      ) : null}
      <g fill="none" stroke={paint} strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 20 L100 72 L100 184 L14 236 Z" strokeWidth="20" />
        <path d="M103 128 L234 18" strokeWidth="30" />
        <path d="M103 128 L234 238" strokeWidth="30" />
      </g>
      <circle cx="76" cy="128" r="9" fill={paint} />
    </svg>
  );

  if (!showWordmark) return <span className={className}>{symbol}</span>;

  /*
   * The wordmark is drawn, not set: a geometric monoline face of circles and
   * straight strokes, which no font here matches. Metrics are in the build
   * script — stroke 13, x-height top 61.5, baseline 148.5, bowl radius 43.5.
   */
  const word = (
    <svg
      viewBox="-7 -7 570 169"
      height={size * 0.62}
      role="img"
      aria-label="Kairelo"
      style={{ display: 'block', flex: 'none' }}
    >
      <g
        fill="none"
        stroke="var(--text)"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* k */}
        <path d="M6.5 6.5 V148.5 M6.5 107 L58 61.5 M6.5 107 L60 148.5" />
        {/* a */}
        <g transform="translate(88 0)">
          <circle cx="43.5" cy="105" r="43.5" />
          <path d="M87 61.5 V148.5" />
        </g>
        {/* i */}
        <path d="M209.5 61.5 V148.5" />
        {/* r */}
        <g transform="translate(240 0)">
          <path d="M6.5 61.5 V148.5 M6.5 92 Q6.5 61.5 44 61.5" />
        </g>
        {/* e */}
        <g transform="translate(312 0)">
          <path d="M0 105 H87" />
          <path d="M87 105 A43.5 43.5 0 1 0 71.5 138.3" />
        </g>
        {/* l */}
        <path d="M433.5 6.5 V148.5" />
        {/* o */}
        <circle cx="505.5" cy="105" r="43.5" />
      </g>
      {/* The dot on the i is the one accent in the wordmark. */}
      <circle cx="209.5" cy="32" r="8.5" fill={variant === 'mono' ? 'currentColor' : '#16c5ad'} />
    </svg>
  );

  return (
    <span className={className ? `logo ${className}` : 'logo'}>
      {symbol}
      {word}
    </span>
  );
}
